import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import QrCodeScannerOutlinedIcon from '@mui/icons-material/QrCodeScannerOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import jsQR from 'jsqr';
import LandingPageLayout from '../../Layout/LandingPageLayout';
import ProfileAvatar from '../../assets/Profile_Avatar.png';
import { useValidateMembership } from '../../services/mutations';

const profileIdPattern =
  /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i;

const isValidProfileId = (value) =>
  profileIdPattern.test(value) && value.match(profileIdPattern)?.[0] === value;

const getProfileIdFromQrValue = (value) => value.match(profileIdPattern)?.[0];

const ValidateMembershipPage = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanFrameRef = useRef(null);
  const scanCountRef = useRef(0);
  const validateMembership = useValidateMembership();
  const [scannerActive, setScannerActive] = useState(false);
  const [scannerMessage, setScannerMessage] = useState('');
  const [result, setResult] = useState(null);

  const stopCamera = () => {
    if (scanFrameRef.current) {
      cancelAnimationFrame(scanFrameRef.current);
      scanFrameRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    setScannerActive(false);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    return stopCamera;
  }, []);

  const validateId = (profileId) => {
    const normalizedId = getProfileIdFromQrValue(profileId.trim());

    if (!isValidProfileId(normalizedId)) {
      stopCamera();
      setResult({
        valid: false,
        status: 'invalid_code',
        message: 'Invalid membership code',
        payload: null,
      });
      return;
    }

    stopCamera();
    setScannerMessage('');
    validateMembership.mutate(
      { profile_id: normalizedId },
      {
        onSuccess: (response) => {
          setResult(response?.data?.payload);
        },
        onError: () => {
          setResult({
            valid: false,
            status: 'error',
            message: 'Unable to validate membership',
            payload: null,
          });
        },
      },
    );
  };

  const scanFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) {
      return;
    }

    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      const width = video.videoWidth;
      const height = video.videoHeight;

      if (width && height) {
        scanCountRef.current += 1;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) {
          scanFrameRef.current = requestAnimationFrame(scanFrame);
          return;
        }

        const decodeCanvas = (sourceX, sourceY, sourceWidth, sourceHeight) => {
          canvas.width = sourceWidth;
          canvas.height = sourceHeight;
          context.drawImage(
            video,
            sourceX,
            sourceY,
            sourceWidth,
            sourceHeight,
            0,
            0,
            sourceWidth,
            sourceHeight,
          );

          const imageData = context.getImageData(
            0,
            0,
            sourceWidth,
            sourceHeight,
          );

          return jsQR(imageData.data, sourceWidth, sourceHeight, {
            inversionAttempts: 'attemptBoth',
          });
        };

        const cropSize = Math.floor(Math.min(width, height) * 0.82);
        const cropX = Math.floor((width - cropSize) / 2);
        const cropY = Math.floor((height - cropSize) / 2);
        const code =
          decodeCanvas(cropX, cropY, cropSize, cropSize) ||
          (scanCountRef.current % 6 === 0
            ? decodeCanvas(0, 0, width, height)
            : null);

        if (code?.data) {
          validateId(code.data);
          return;
        }
      }
    }

    scanFrameRef.current = requestAnimationFrame(scanFrame);
  };

  const startScanner = async () => {
    setResult(null);
    setScannerMessage('');

    if (!navigator.mediaDevices?.getUserMedia) {
      setScannerMessage(
        'Camera access was not available. Open this page in a browser with camera support.',
      );
      return;
    }

    setScannerActive(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      scanCountRef.current = 0;
      await videoRef.current.play();
      scanFrameRef.current = requestAnimationFrame(scanFrame);
    } catch (error) {
      setScannerMessage(
        'Camera access was not available. Allow camera permission and try again.',
      );
      stopCamera();
    }
  };

  const profile = result?.payload;
  const isLoading = validateMembership.isLoading;
  const statusColor = result?.valid ? '#22A887' : '#F46B1B';

  const resultView = result && !isLoading && (
    <Box
      sx={{
        border: `1px solid ${statusColor}`,
        background: '#050505',
        p: 3,
        minHeight: 280,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      <Avatar
        src={profile?.profile_pic || ProfileAvatar}
        alt={profile?.name || 'Member'}
        sx={{ width: 112, height: 112, mb: 2 }}
      />
      <Typography
        sx={{
          color: statusColor,
          fontFamily: 'Gill Sans Nova',
          fontSize: 18,
          fontWeight: 900,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
        }}>
        {result.message}
      </Typography>
      <Typography
        sx={{
          mt: 1,
          fontFamily: 'Gill Sans Nova',
          fontSize: { xs: 28, sm: 34 },
          fontWeight: 900,
        }}>
        {profile?.name || 'Unknown Member'}
      </Typography>
      {profile && (
        <Typography
          sx={{
            mt: 1,
            color: '#cfcfcf',
            fontFamily: 'Gill Sans Nova',
            fontSize: 18,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
          }}>
          {profile.role} / {profile.member_type}
        </Typography>
      )}
    </Box>
  );

  const scannerView = (
    <Box
      onClick={!scannerActive && !result ? startScanner : undefined}
      role={!scannerActive && !result ? 'button' : undefined}
      tabIndex={!scannerActive && !result ? 0 : undefined}
      onKeyDown={(event) => {
        if (
          !scannerActive &&
          !result &&
          (event.key === 'Enter' || event.key === ' ')
        ) {
          event.preventDefault();
          startScanner();
        }
      }}
      sx={{
        position: scannerActive ? 'fixed' : 'relative',
        inset: scannerActive ? 0 : 'auto',
        zIndex: scannerActive ? 1500 : 'auto',
        overflow: 'hidden',
        border: scannerActive ? 0 : '1px solid rgba(255,255,255,0.25)',
        minHeight: scannerActive ? '100dvh' : 280,
        width: '100%',
        cursor: !scannerActive && !result ? 'pointer' : 'default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#050505',
        color: 'inherit',
        font: 'inherit',
        textAlign: 'center',
        '&:hover': !scannerActive && !result
          ? { borderColor: 'rgba(244,107,27,0.85)' }
          : undefined,
      }}>
      <video
        ref={videoRef}
        muted
        playsInline
        style={{
          width: '100%',
          height: scannerActive ? '100%' : 'auto',
          minHeight: scannerActive ? '100dvh' : 'auto',
          display: scannerActive ? 'block' : 'none',
          objectFit: 'cover',
        }}
      />
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      {resultView}
      {scannerActive && (
        <>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(transparent, transparent), rgba(0,0,0,0.2)',
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              width: { xs: '76vw', sm: 360 },
              maxWidth: 420,
              aspectRatio: '1 / 1',
              border: '3px solid rgba(255,255,255,0.9)',
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.42)',
              pointerEvents: 'none',
            }}
          />
          <Typography
            sx={{
              position: 'absolute',
              left: 24,
              right: 24,
              bottom: { xs: 34, sm: 44 },
              color: '#fff',
              fontFamily: 'Gill Sans Nova',
              fontWeight: 900,
              fontSize: 18,
              textAlign: 'center',
              textShadow: '0 2px 12px rgba(0,0,0,0.8)',
            }}>
            Hold the membership QR inside the square
          </Typography>
          <Typography
            sx={{
              position: 'absolute',
              left: 24,
              right: 24,
              top: { xs: 78, sm: 92 },
              color: '#fff',
              fontFamily: 'Gill Sans Nova',
              fontSize: 14,
              letterSpacing: '0.14em',
              textAlign: 'center',
              textShadow: '0 2px 12px rgba(0,0,0,0.8)',
              textTransform: 'uppercase',
            }}>
            Scanning...
          </Typography>
          <Button
            variant="contained"
            startIcon={<CloseOutlinedIcon />}
            onClick={stopCamera}
            sx={{
              position: 'absolute',
              top: { xs: 18, sm: 24 },
              right: { xs: 18, sm: 24 },
              borderRadius: 999,
              background: 'rgba(0,0,0,0.72)',
              color: '#fff',
              px: 2.5,
              fontFamily: 'Gill Sans Nova',
              fontWeight: 900,
              '&:hover': { background: 'rgba(0,0,0,0.9)' },
            }}>
            Close
          </Button>
        </>
      )}
      {!scannerActive && !result && (
        <Box sx={{ color: '#cfcfcf', px: 3 }}>
          <QrCodeScannerOutlinedIcon sx={{ fontSize: 72, mb: 1 }} />
          <Typography sx={{ fontFamily: 'Gill Sans Nova' }}>
            Scan a Club Membership Card QR code.
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <LandingPageLayout>
      <Box
        sx={{
          minHeight: '100vh',
          background: '#000',
          display: 'flex',
          justifyContent: 'center',
          px: 2,
          pt: { xs: 16, sm: 18 },
          pb: 8,
        }}>
        <Box
          sx={{
            width: '100%',
            maxWidth: 700,
            color: '#fff',
            textAlign: 'center',
          }}>
          <Typography
            sx={{
              fontFamily: 'Gill Sans Nova',
              fontWeight: 900,
              fontSize: { xs: 28, sm: 36 },
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}>
            Validate Membership
          </Typography>

          <Box
            sx={{
              mt: 4,
              border: '1px solid rgba(255,255,255,0.45)',
              background: '#050505',
              p: { xs: 2, sm: 3 },
            }}>
            {result && !isLoading && (
              <Button
                variant="contained"
                startIcon={<ReplayOutlinedIcon />}
                onClick={() => {
                  setResult(null);
                  startScanner();
                }}
                sx={{
                  mb: 3,
                  borderRadius: 999,
                  background: '#F46B1B',
                  color: '#fff',
                  px: 4,
                  fontFamily: 'Gill Sans Nova',
                  fontWeight: 900,
                  '&:hover': { background: '#d95a12' },
                }}>
                Scan another
              </Button>
            )}

            {!result && !scannerActive && (
              <Typography
                sx={{
                  mb: 2,
                  color: '#9b9b9b',
                  fontFamily: 'Gill Sans Nova',
                  fontSize: 14,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                }}>
                Tap the square to open the camera
              </Typography>
            )}

            {scannerView}

            {scannerMessage && (
              <Alert severity="info" sx={{ mt: 3, textAlign: 'left' }}>
                {scannerMessage}
              </Alert>
            )}

          </Box>

          {isLoading && (
            <Box sx={{ mt: 4 }}>
              <CircularProgress sx={{ color: '#F46B1B' }} />
            </Box>
          )}
        </Box>
      </Box>
    </LandingPageLayout>
  );
};

export default ValidateMembershipPage;
